import get from 'lodash/get';
import includes from 'lodash/includes';
import uniq from 'lodash/uniq';
import { questionTypes, MAX_ALLOWED_FILE_SIZE, type QnAStateType, validationTypes } from '../../reducers';
import { isEmpty, checkFileType, getSizeInBytes, getSizeInUnit, extractSizeAttr } from '../../utils/answer';
import { getQuestionById, getTopQuestionIds, getQuestionValidations, getQuestionValidationOfType } from './questions';
import { getUserAnswersByQuestionId, getUserAnswers, getUserNoteByQuestionId } from './answers';
import { getFollowUpQuestionIdsByAnswers } from './followup-questions';
import { getWordCount } from '../../utils/question';
import { getRawTextFromMarkdown } from '@uber/rich-text-editor-lib/src/components/lexical-editor/utils/utils';
export const isNoteRequired = (state: QnAStateType, questionId: string) => {
  const question = getQuestionById(state, questionId);
  const selectedAnswers = getUserAnswersByQuestionId(state, questionId);
  if (question && selectedAnswers) {
    return includes(question.answers.filter(answerItem => selectedAnswers.includes(answerItem.answer)).map(answerItem => answerItem.note), 'required');
  }
  return false;
};

// return true when note is INVALID
export const isNoteInvalidWithCustomValidators = (state: QnAStateType, questionId: string, userNote?: string): boolean => {
  const question = getQuestionById(state, questionId);
  const answerOptions = get(question, 'answers');
  const userAnswers = getUserAnswersByQuestionId(state, questionId);
  const userAnswersMap = getUserAnswers(state);
  if (!answerOptions || !userAnswers || !question) {
    return false;
  }
  const selectedAnswerOptions = uniq(answerOptions.filter(answerItem => {
    return userAnswers.includes(answerItem.answer);
  }));
  return selectedAnswerOptions.some(answerOption => {
    const noteValidatorInString = get(answerOption, 'noteValidator');
    const noteRequiredOptionValue = get(answerOption, 'note');
    if (!noteValidatorInString) {
      return false;
    }
    try {
      const validator = eval(noteValidatorInString);

      // return true when it's INVALID
      return !validator(userNote, noteRequiredOptionValue, question, userAnswersMap, state);
    } catch (e) {
      // skipping on any runtime error
      return false;
    }
  });
};

/** Returns list of questions that are rendered for current answers. i.e topQuestions + followUps based on current answers */
const getAllQuestionIdsByAnswers = (state: QnAStateType) => {
  const topQuestionIds = getTopQuestionIds(state);
  let result = [...topQuestionIds];
  topQuestionIds.forEach(id => {
    result = result.concat(getAllFollowUpQuestionIdsByAnswers(state, id));
  });
  return result;
};
const getAllFollowUpQuestionIdsByAnswers = (state: QnAStateType, questionId: string) => {
  const followUpIds = getFollowUpQuestionIdsByAnswers(state, questionId);
  let result = [...followUpIds];
  followUpIds.forEach(id => {
    result = result.concat(getAllFollowUpQuestionIdsByAnswers(state, id));
  });
  return result;
};
export const getAllAnswered = (state: QnAStateType, requiredQuestionsOnly = false): boolean => {
  let allRenderedQuestionIds = getAllQuestionIdsByAnswers(state);
  if (requiredQuestionsOnly) {
    allRenderedQuestionIds = allRenderedQuestionIds.filter(id => getQuestionById(state, id)?.isRequired);
  }
  if (isEmpty(allRenderedQuestionIds)) {
    // return true when there are no questions
    return true;
  }
  let hasIncompleteAnswer = false;
  for (const questionId of allRenderedQuestionIds) {
    const question = getQuestionById(state, questionId);
    const answers = getUserAnswersByQuestionId(state, questionId);
    const note = getUserNoteByQuestionId(state, questionId);
    const unAnswered =
    // question not answered
    isEmpty(answers) ||
    // empty answer for input type question
    // @ts-expect-error todo(ts-migration) TS2339 Property 'length' does not exist on type 'string | File'.
    question.type !== questionTypes.file && !answers[0].length ||
    // note not provided when it's required
    isNoteRequired(state, questionId) && !note ||
    // custom note validator not passing
    isNoteInvalidWithCustomValidators(state, questionId, note);
    if (unAnswered) {
      hasIncompleteAnswer = true;
      break;
    }
  }
  return !hasIncompleteAnswer;
};
export function hasValidationErrors(qnaState: QnAStateType): boolean {
  const allRenderedQuestionIds = getAllQuestionIdsByAnswers(qnaState);
  return allRenderedQuestionIds.some(questionId => validateQuestion(qnaState, questionId)?.length);
}
export const ValidationMessages = {
  answerRequired: 'Required question is unanswered',
  answerInvalidNumber: 'Number type question can only contain valid numbers',
  noteRequired: 'The selected answer requires a note',
  noteInvalid: `The added note doesn't match the validation`,
  getMinValMessage: (minVal: string) => `Value should be greater than ${minVal}`,
  getMaxValMessage: (maxVal: string) => `Value should be less than ${maxVal}`,
  getRegexMessage: (regex: string) => `Answer should match the pattern - ${regex}`,
  getMaxAllowedFileSize: (maxSize: string) => `Max allowed file size cannot exceed ${maxSize}`,
  getMaxFileSizeMessage: (fileSize: string, maxSize: string) => `File is too large (${fileSize}). Max allowed file size is ${maxSize}`,
  getAcceptedFileTypesMessage: (fileType: string, acceptedTypes: string) => `File type "${fileType}" is not allowed. Only following file types are accepted: ${acceptedTypes}`,
  getMinWordsCountMessage: (minWordsCount: string) => `Answer should have at least ${minWordsCount} words`,
  getMaxWordsCountMessage: (maxWordsCount: string) => `Answer should have no more than ${maxWordsCount} words`
};
export function validateQuestion(qnaState: QnAStateType, questionId: string): string | undefined | null {
  const question = qnaState.questions[questionId];
  if (!question) {
    return null;
  }
  const userAnswers = qnaState.answers[questionId]?.answers;

  // validate if question is required and not answered
  if (question.isRequired && (isEmpty(userAnswers) || isEmpty(userAnswers[0]))) {
    return ValidationMessages.answerRequired;
  }
  const answer = userAnswers?.[0];
  if (question.type === questionTypes.file || answer instanceof File) {
    // validation will only be done freshly uploaded file (File object in memory) not on file's url once it is uploaded
    if (answer instanceof File) {
      const maxFileSize = getQuestionValidationOfType(qnaState, questionId, validationTypes.MAX_FILE_SIZE)?.value || getSizeInUnit(MAX_ALLOWED_FILE_SIZE, 'MB');
      if (maxFileSize && answer.size > getSizeInBytes(maxFileSize)) {
        return ValidationMessages.getMaxFileSizeMessage(getSizeInUnit(answer.size, extractSizeAttr(maxFileSize).unit, 3), maxFileSize);
      }
      const acceptedFileTypes = getQuestionValidationOfType(qnaState, questionId, validationTypes.ACCEPTED_FILE_TYPES)?.value;
      if (acceptedFileTypes && !acceptedFileTypes.split(',').some(type => checkFileType(answer, type.trim()))) {
        const ext = answer.name.split('.').slice(-1)[0];
        return ValidationMessages.getAcceptedFileTypesMessage(ext, acceptedFileTypes);
      }
    }
  } else if (!isEmpty(answer) && getQuestionValidations(qnaState, questionId).length) {
    const rawAnswer = question.type === questionTypes.richText ? getRawTextFromMarkdown(answer) : answer;
    const minValue = getQuestionValidationOfType(qnaState, questionId, validationTypes.MINVAL)?.value;
    if (minValue && Number(answer) < Number(minValue)) {
      return ValidationMessages.getMinValMessage(minValue);
    }
    const maxValue = getQuestionValidationOfType(qnaState, questionId, validationTypes.MAXVAL)?.value;
    if (maxValue && Number(answer) > Number(maxValue)) {
      return ValidationMessages.getMaxValMessage(maxValue);
    }
    const maxWords = getQuestionValidationOfType(qnaState, questionId, validationTypes.MAX_WORDS)?.value;
    if (maxWords && getWordCount(rawAnswer) > Number(maxWords)) {
      return ValidationMessages.getMaxWordsCountMessage(maxWords);
    }
    const minWords = getQuestionValidationOfType(qnaState, questionId, validationTypes.MIN_WORDS)?.value;
    if (minWords && getWordCount(rawAnswer) < Number(minWords)) {
      return ValidationMessages.getMinWordsCountMessage(minWords);
    }
    const regex = getQuestionValidationOfType(qnaState, questionId, validationTypes.REGEXP)?.value;
    if (regex && !new RegExp(regex).test(answer)) {
      return ValidationMessages.getRegexMessage(regex);
    }
  }
  const answerNote = getUserNoteByQuestionId(qnaState, questionId);
  // validate if required note is not added
  if (isNoteRequired(qnaState, questionId) && !answerNote) {
    return ValidationMessages.noteRequired;
  }

  // check if note matches the custom validation
  if (isNoteInvalidWithCustomValidators(qnaState, questionId, answerNote)) {
    return ValidationMessages.noteInvalid;
  }
}
export function getValidationErrorMessage(qnaState: QnAStateType, questionId: string): string | undefined | null {
  const question = qnaState.questions[questionId];
  if (!question || !question.showValidationErrors) {
    return null;
  }
  return validateQuestion(qnaState, questionId);
}